import { push } from 'react-router-redux';

import { toastr } from 'react-redux-toastr';
import CheckoutAPI from '../apis/checkout';
import CheckoutStepCalculator from '../services/checkout-step-calculator';
import InvalidCheckoutStepException from '../errors/invalid-checkout-step';
import InvalidOrderTransitionException from '../errors/invalid-order-transition';

import loader from './loader';
import order from './order';
import errorActions from './error';

import APP_ROUTES from '../constants/app-routes';
import APP_ACTIONS from '../constants/app-actions';
import translations from '../../translations/app-translations';
import cartState from './cart-state';

import { HTTP_ERRORS } from '../constants';
import CheckoutDataCalculator from '../services/checkout-data-calculator';

const checkout = {
    goToPrevStep: (orderObj, locale, currentStep) => async (dispatch, getState) => {
        // Show Loader
        // Send prev request to API.
        // If success ->
        //    reset the order
        //    re-route to the prev step.
        //    hide loader
        // If failed ->
        //    Do not touch the order.
        //    hide loader

        // Edge Cases:
        // Return if order is already complete.

        dispatch(loader.displayLoader());

        const orderState = orderObj.state;
        const checkoutSteps = orderObj.checkout_steps;

        if (CheckoutStepCalculator.isLastStep(checkoutSteps, orderState)) {
            // TODO: Show message
            // dispatch(flash.showFlash('Your order has already been placed. Thanks!'));
            dispatch(push(APP_ROUTES.homePageRoute));
        } else {
            const prevStep = CheckoutStepCalculator.previous(checkoutSteps, currentStep);
            const predecessorStep = CheckoutStepCalculator.previous(checkoutSteps, prevStep);

            const formData = CheckoutDataCalculator.calculateFormToStep(orderObj, predecessorStep);

            const { token } = getState().order;

            const apiPromise = CheckoutAPI.update({ token, locale, formData });

            await apiPromise.then(
                async (response) => {
                    dispatch(order.updateOrderInState(response.body));

                    const newOrder = getState().order;
                    await dispatch(push(await checkout._fetchNextRoute(newOrder, predecessorStep, token, locale, dispatch, getState)));
                    dispatch(loader.hideLoader());
                },
                (error) => {
                    dispatch(loader.hideLoader());
                    toastr.error(translations[locale].error, error.response.body.error);
                },
            );

            return apiPromise;
        }
    },

    goToNextStep: (orderObj, locale, formData = {}, shouldRedirect = true, options = {}) => async (dispatch, getState) => {
        // Show Loader
        // Send next request to API.
        // If success ->
        //    reset the order
        //    re-route to the next step.
        //    hide loader
        // If failed ->
        //    Do not touch the order.
        //    hide loader

        // Edge Cases:
        // Return if order is already complete.

        dispatch(loader.displayLoader());

        const orderState = orderObj.state;
        const checkoutSteps = orderObj.checkout_steps;

        if (CheckoutStepCalculator.isLastStep(checkoutSteps, orderState)) {
            // TODO: Show message
            // dispatch(flash.showFlash('Your order has already been placed. Thanks!'));
            dispatch(push(APP_ROUTES.homePageRoute));
        } else {
            const currentStep = getState().currentCheckoutStep;
            const { token } = getState().order;

            const apiPromise = CheckoutAPI.update({ token, locale, formData });

            await apiPromise.then(
                async (response) => {
                    if (currentStep === 'address') {
                        await CheckoutAPI.getPaymentMethods({ locale, token }).then((response) => {
                            const paymentMethods = response.body.data;

                            dispatch(checkout.addPaymentMethods(paymentMethods));
                        });
                    }

                    dispatch(order.updateOrderInState(response.body));

                    const newOrder = getState().order;
                    await dispatch(push(await checkout._fetchNextRoute(newOrder, currentStep, token, locale, dispatch, getState, shouldRedirect, options)));
                    dispatch(loader.hideLoader());
                },
                (error) => {
                    dispatch(loader.hideLoader());
                    toastr.error(translations[locale].error, error.response.body.error);
                },
            );

            return apiPromise;
        }
    },

    addPaymentMethods: (paymentMethodsResponse) => ({
        type: APP_ACTIONS.ADD_PAYMENT_METHOD,
        payload: paymentMethodsResponse,
    }),

    getRedsysInfo: () => async (dispatch, getState) => {
        const { token } = getState().order;
        const response = await CheckoutAPI.getRedsysInfo({ token });
        dispatch({ type: APP_ACTIONS.ADD_REDSYS_PAYMENT_INFO, payload: response.body });
    },
    addRedsysPaymentError: (error) => ({ type: APP_ACTIONS.ADD_REDSYS_PAYMENT_ERROR, payload: error }),

    setIsFetching: (isFetching) => ({
        type: APP_ACTIONS.SET_FETCHING_DATA,
        payload: isFetching,
    }),

    _fetchNextRoute: async (orderObj, currentStep, token, locale, dispatch, getState, shouldRedirect = true, options = {}) => {
        try {
            return await checkout._nextCheckoutStepRoute(orderObj, currentStep, token, locale, dispatch, getState, shouldRedirect, options);
        } catch (err) {
            if (err instanceof InvalidCheckoutStepException) {
                return APP_ROUTES.cartPageRoute;
            }

            if (err instanceof InvalidOrderTransitionException) {
                return APP_ROUTES.homePageRoute;
            }
        }
    },

    _nextCheckoutStepRoute: async (orderObj, currentStep, token, locale, dispatch, getState, shouldRedirect, options = {}) => {
        const currentStepIndex = orderObj.checkout_steps.indexOf(currentStep.trim());

        /* If currentStep is a valid step and not the last step.
       Last step is 'complete' which means order is placed. */
        if (currentStepIndex > -1 || currentStep === 'cart') {
            if (!CheckoutStepCalculator.isLastStep(orderObj.checkout_steps, currentStep)) {
                const nextStep = CheckoutStepCalculator.next(orderObj.checkout_steps, currentStep, shouldRedirect);
                if (nextStep !== 'complete') {
                    const nextStepIndex = orderObj.checkout_steps.indexOf(nextStep.trim());
                    const orderStepIndex = orderObj.checkout_steps.indexOf(orderObj.state.trim());

                    if (nextStepIndex > orderStepIndex && currentStep !== 'cart') {
                        if (currentStep === 'address') {
                            dispatch(errorActions.clearErrorLineItems());
                        }

                        dispatch(checkout.setIsFetching(true));
                        await CheckoutAPI.next({ token, locale, zone: getState().zones.currentZone }).then(
                            (response) => {
                                dispatch(order.updateOrderInState(response.body));
                                dispatch(push(APP_ROUTES.checkout[`${nextStep}PageRoute`]));
                                dispatch(checkout.setIsFetching(false));
                            },
                            (error) => {
                                if (error && error.response && error.response.status === 422
                                    && error.response.body.errors && error.response.body.errors.shipments
                                    && error.response.body.errors.shipments.length > 0) {
                                    let lineItems = [];
                                    error.response.body.errors.shipments.forEach((items) => {
                                        lineItems = [...lineItems, ...JSON.parse(items)];
                                    });
                                    dispatch(errorActions.doErrorLineItems(lineItems));
                                    dispatch(push(APP_ROUTES.cartPageRoute));
                                } else {
                                    dispatch(push(APP_ROUTES.checkout[`${currentStep}PageRoute`]));
                                    if (currentStep === 'payment') {
                                        toastr.error(translations[locale]['payment-error'], error.response.body.error);
                                    } else {
                                        toastr.error(translations[locale].error, error.response.body.error);
                                    }
                                }
                                dispatch(checkout.setIsFetching(false));
                            },
                        );
                    } else {
                        dispatch(push(APP_ROUTES.checkout[`${nextStep}PageRoute`]));
                    }
                } else {
                    try {
                        await CheckoutAPI.complete({ token, locale, zone: getState().zones.currentZone }).then(
                            () => {
                                dispatch(cartState.setHasExtraTimeCheckout(false));
                                dispatch({ type: APP_ACTIONS.SET_CURRENT_CHECKOUT_STEP, payload: 'complete' });
                                dispatch(push(APP_ROUTES.checkout[`${nextStep}PageRoute`]));
                            },
                            (error) => {
                                const { statusCode, body } = error.response;
                                const incognitoModeErrorCause = 'incognito_mode_not_allowed';
                                if (statusCode === HTTP_ERRORS.HTTP_418 || (statusCode === HTTP_ERRORS.HTTP_422 && body.cause && body.cause === incognitoModeErrorCause)) {
                                    dispatch({ type: APP_ACTIONS.COMPLETE_ORDER_ERROR, payload: body });
                                } else {
                                    toastr.error(translations[locale].error, body.error);
                                }
                                if (options && options.onCompleteError) {
                                    options.onCompleteError(error);
                                }
                            },
                        );
                    } catch (err) {
                        console.log(err);
                        dispatch({ type: APP_ACTIONS.ADD_ORDER_ERROR, payload: 'error' });
                        dispatch(push(APP_ROUTES.checkout.failPageRoute));
                    }
                }
            } else {
                throw new InvalidOrderTransitionException();
            }
        } else {
            throw new InvalidCheckoutStepException();
        }
    },
};

export default checkout;



/** WEBPACK FOOTER **
 ** ./src/actions/checkout.js
 **/